import { useEffect, useState } from "react";
import {
  fetchDashboard,
  readLookupOpen,
  valuationCaption,
  writeLookupOpen,
  type DashboardDto,
} from "../lib/dashboardPage";
import { SectionTitle } from "./Heading";
import { Icon } from "./Icons";

/**
 * "Which card to use": the best card per spend category, at the top of the
 * Cards page (approved mockup, round 3, 2026-09-10).
 *
 * The whole section folds, and stays folded or open the way it was left, for
 * the times the job is only "look at my cards". It fetches on first unfold,
 * so a folded list costs no request. Every card named under a category opens
 * that card's sheet, each name of a tie included. Names that cannot be paired
 * one-to-one with the ids (a card name containing ", ") stay plain text.
 */
export function WhichCard({
  knownCardIds,
  onOpenCard,
}: {
  knownCardIds: ReadonlySet<number>;
  onOpenCard: (id: number) => void;
}) {
  const [open, setOpen] = useState(readLookupOpen);
  const [data, setData] = useState<DashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || data) return;
    let cancelled = false;
    fetchDashboard()
      .then((d) => {
        if (cancelled) return;
        setError(null);
        setData(d);
      })
      .catch((e: unknown) => {
        // Surfaced, never an empty list: no rows looks like no cards.
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, data]);

  const toggle = () => {
    setOpen(!open);
    writeLookupOpen(!open);
  };
  const rowCount = data?.best_card_groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <section>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`flex min-h-11 w-full items-center gap-3 text-left ${
          open ? "" : "border-b border-neutral-800/70"
        }`}
      >
        <Icon name="chevron" size={16} className="chev shrink-0 text-neutral-500" />
        <span className="font-semibold">Which card to use</span>
        {rowCount !== undefined && (
          <span className="ml-auto text-xs text-neutral-400">{rowCount} categories</span>
        )}
      </button>

      {open && error && (
        <div className="mt-2 rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          Could not load the category list: {error}
        </div>
      )}
      {open && !error && !data && (
        <p className="mt-2 text-sm text-neutral-400">Loading…</p>
      )}
      {open && data && (
        <div className="mt-2 space-y-5">
          {data.best_card_groups.map((g) => (
            <section key={g.label}>
              <SectionTitle>{g.label}</SectionTitle>
              <ul className="mt-1 border-t border-neutral-800/70">
                {g.rows.map((r, i) => {
                  const ids = g.row_cards?.[i] ?? [];
                  // The engine joins a tie's names with ", " in id order.
                  const names = r["Best Card"].split(", ");
                  const linkable =
                    ids.length > 0 &&
                    names.length === ids.length &&
                    ids.every((id) => knownCardIds.has(id));
                  return (
                    <li
                      key={`${g.label}-${i}`}
                      // grid-cols-[minmax(0,1fr)_auto]: a bare column sizes to
                      // the longest card name and pushes the rate off a phone.
                      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 border-b border-neutral-800/70 py-3"
                    >
                      <span className="font-medium">{r["Category"]}</span>
                      <span className="row-span-2 self-center text-right">
                        <span className="hero-num block text-xl">{r["Rate"]}</span>
                        {r["Est. ¢/$"] && (
                          <span className="block text-[11px] tabular-nums text-neutral-500">
                            {r["Est. ¢/$"]}
                          </span>
                        )}
                      </span>
                      {linkable ? (
                        // Chips, not dotted-underline text: each opens its card's
                        // sheet, and a tie is one chip per card (restyle, 2026-09-12).
                        <span className="flex flex-wrap gap-1.5 pt-1">
                          {ids.map((id, j) => (
                            <button key={id} type="button" onClick={() => onOpenCard(id)} className="card-chip">
                              {names[j]}
                              <Icon name="chevron" size={13} strokeWidth={2} className="text-neutral-500" />
                            </button>
                          ))}
                        </span>
                      ) : (
                        <span className="text-sm text-neutral-300">{r["Best Card"]}</span>
                      )}
                      {r["Cap / Notes"] && (
                        <span className="col-span-2 text-xs text-neutral-500">{r["Cap / Notes"]}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {data.best_card_groups.length > 0 && (
            <p className="text-xs text-neutral-500">{valuationCaption(data.valuation_date)}</p>
          )}
        </div>
      )}
    </section>
  );
}
